import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { apiClient } from '../../services/apiClient';
import { WizardComponentProps } from '../../types';

const ValidationPanel: React.FC<WizardComponentProps> = ({ isEmbedded }) => {
  const { customerId, phaseId } = useParams<{ customerId: string; phaseId: string }>();
  const [validationResults, setValidationResults] = useState<any>(null);

  const validateMappingsMutation = useMutation({
    mutationFn: () => apiClient.validateMappings(customerId!, phaseId!),
    onSuccess: (data) => {
      setValidationResults(data);
      toast.success('Validation completed');
    },
    onError: (error: any) => {
      toast.error('Validation failed');
    }
  });

  const validateDataMutation = useMutation({
    mutationFn: () => apiClient.validateData(customerId!, phaseId!),
    onSuccess: (data) => {
      setValidationResults(data);
      toast.success('Data validation completed');
    },
    onError: (error: any) => {
      toast.error('Data validation failed');
    }
  });

  return (
    <div className="max-w-6xl mx-auto">
      {!isEmbedded && (
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Data Validation</h1>
          <p className="text-gray-600 mt-2">Validate field mappings and data quality</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <button
          onClick={() => validateMappingsMutation.mutate()}
          disabled={validateMappingsMutation.isPending}
          className="p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow text-left"
        >
          <CheckCircleIcon className="w-8 h-8 text-green-600 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Validate Mappings</h3>
          <p className="text-gray-600">Check field mapping completeness and accuracy</p>
        </button>

        <button
          onClick={() => validateDataMutation.mutate()}
          disabled={validateDataMutation.isPending}
          className="p-6 bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow text-left"
        >
          <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600 mb-4" />
          <h3 className="text-lg font-semibold mb-2">Validate Data</h3>
          <p className="text-gray-600">Check data quality and format issues</p>
        </button>
      </div>

      {validationResults && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Validation Results</h2>

          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {validationResults.valid ? '✓' : '✗'}
              </div>
              <div className="text-sm text-gray-600">Overall Status</div>
            </div>

            <div className="text-center p-4 bg-red-50 rounded-lg">
              <div className="text-2xl font-bold text-red-600">
                {validationResults.errors?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Errors</div>
            </div>

            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {validationResults.warnings?.length || 0}
              </div>
              <div className="text-sm text-gray-600">Warnings</div>
            </div>
          </div>

          {validationResults.errors && validationResults.errors.length > 0 && (
            <div className="mb-4">
              <h3 className="text-lg font-medium text-red-800 mb-2">Errors</h3>
              {validationResults.errors.map((error: any, index: number) => (
                <div key={index} className="validation-error mb-2">
                  <XCircleIcon className="w-5 h-5 text-red-600 inline mr-2" />
                  {error.message || error}
                </div>
              ))}
            </div>
          )}

          {validationResults.warnings && validationResults.warnings.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-yellow-800 mb-2">Warnings</h3>
              {validationResults.warnings.map((warning: any, index: number) => (
                <div key={index} className="validation-warning mb-2">
                  <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 inline mr-2" />
                  {warning.message || warning}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ValidationPanel;
